import * as turf from "@turf/turf";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type {
  Question,
  QuestionValue,
  Result,
} from "senf-atomic-design-system/src/components/special/survey/survey.types";
import { parseGeoData } from "senf-shared";
import type { Idea } from "senf-shared/types";
import i18n from "senf-shared/utils/i18n";

type ResolvedProperties = {
  value?: string;
  description?: string;
  tags?: {
    columnId: string;
    columnName: string;
    value?: string;
  }[];
};

export const resolveResultIds = ({
  properties,
  question,
}: {
  properties: GeoJsonProperties;
  question?: Question;
}): ResolvedProperties => {
  const resolved = { ...properties };

  if (question?.options && properties?.choices) {
    resolved.value =
      question.options.find(({ uid }) => uid === properties.choices)?.value ||
      (question.mapbasedOtherOption &&
        (properties.comment || question.otherTitle || i18n.t("global:other")));
  }
  if (question?.resultsTableColumns && properties?.tags) {
    resolved.tags = properties.tags.map(
      (tag: { key: string; value: string }) => {
        const { values, label } =
          question.resultsTableColumns?.find(
            ({ value }) => value === tag.key,
          ) || {};
        return {
          columnId: tag.key,
          columnName: label,
          value: values?.find((e) => e.value === tag.value)?.label,
        };
      },
    );
  }
  return resolved;
};
type ReferenceItem = {
  withAI: boolean;
  label: string;
  type: string;
  value: string;
  values?: { label: string; value: string }[];
};

type InputItem = {
  key: string;
  value: string;
};

type OutputItem = {
  label: string;
  value: string;
};

function resolveLabelsAndValues(
  reference: ReferenceItem[],
  input?: InputItem[],
): OutputItem[] {
  const valueMap = new Map<string, string>();

  for (const item of reference) {
    valueMap.set(item.value, item.label);

    if (item.values) {
      for (const subItem of item.values) {
        valueMap.set(subItem.value, subItem.label);
      }
    }
  }

  return (
    input?.map(({ key, value }) => ({
      label: valueMap.get(key) || key,
      value: valueMap.get(value) || value,
    })) || []
  );
}
export const resolveIdeaIds = ({
  properties,
  ideaMap,
}: {
  properties: GeoJsonProperties;
  ideaMap;
}): ResolvedProperties => {
  const resolved = { ...properties };

  if (properties?.tags) {
    for (const [tagIndex, tag] of resolveLabelsAndValues(
      ideaMap.ideaTableColumns,
      properties.tags,
    ).entries()) {
      resolved[`tag-${tagIndex + 1}-${tag.label}`] = tag.value;
    }
  }
  if (ideaMap?.comments) {
    for (const [commentIndex, comment] of ideaMap?.comments
      ?.filter(({ ideaId }) => properties?.ideaId === ideaId)
      .entries() || []) {
      resolved[`comment-${commentIndex + 1}`] = comment.body;
    }
  }
  return resolved;
};

const augmentResultProperties = ({
  properties,
  result,
}: {
  properties: GeoJsonProperties;
  result?: Partial<Result | QuestionValue>;
}) => {
  if (!result) return properties;
  const augmented = { ...properties };

  if ("userData" in result) {
    for (const [key, value] of Object.entries(
      sanitizeProperties(result.userData || {}, {
        include: ["createdAt", "age", "userId", "postcode"],
      }),
    )) {
      augmented[`userData-${key}`] = value;
    }
  }

  if ("description" in result && result.description && properties?.tags) {
    augmented.value = result.description;
  }

  if ("timeStamp" in result && result.timeStamp) {
    augmented.date = new Date(result.timeStamp).toLocaleString(
      i18n.resolvedLanguage || "de",
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "numeric",
      },
    );
  }
  if ("viewed" in result && result.viewed) {
    augmented.viewed = new Date(result?.viewed).toLocaleString(
      i18n.resolvedLanguage || "de",
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "numeric",
      },
    );
  }
  return augmented;
};

/**
 * If exclude is provided, it will take precedence over include.
 */
const sanitizeProperties = (
  properties: NonNullable<GeoJsonProperties>,
  options: {
    exclude?: string[];
    include?: string[];
    type?: Geometry["type"];
  } = { exclude: [], include: [] },
) => {
  const sanitized: NonNullable<GeoJsonProperties> = options.include
    ? {}
    : { ...properties };
  for (const key of options.include || []) {
    sanitized[key] = properties[key];
  }
  for (const key of options.exclude || []) {
    if (sanitized[key]) delete sanitized[key];
  }
  if (properties?.comment) sanitized.value = i18n.t("global:other");
  if (properties?.description) sanitized.value = properties.description;
  if (properties?.color && options.type) {
    if (options.type === "Point") sanitized["marker-color"] = properties.color;
    if (options.type === "LineString" || options.type === "Polygon")
      sanitized.stroke = properties.color;
    if (options.type === "Polygon") {
      sanitized.fill = properties.color;
      sanitized["fill-opacity"] = 0.2;
    }
  }
  return sanitized;
};

/**
 * If flatten is true, it will return a FeatureCollection with all nested FeatureCollections flattened.
 * This is not recommended, because it will duplicate the properties of the nested Feature Collection
 * for each Feature.
 */

export const prepareIdeaMapResults = (
  { ideas, ideaMap }: { ideas?: Idea[]; ideaMap },
  wrapInFeatureCollection = false,
) => {
  if (!ideas?.length) return [{}];
  // console.log(ideas, ideaMap);
  const features = ideas.flatMap((idea: Idea) => {
    const f =
      idea.geoData?.features ||
      (idea.long &&
        idea.lat && [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [idea.long, idea.lat], // Ensure these are numbers
            },
          },
        ]) ||
      [];
    return f
      .map((feature) => {
        const properties = { ...feature.properties, ...idea };
        const sanitizedProperties = sanitizeProperties(properties, {
          include: [
            "createdAt",
            "title",
            "body",
            "ideaId",
            "likeCount",
            "commentCount",
            "municipalityName",
            "localityName",
            "postcodeName",
            "countryName",
            "topicLabel",
            "color",
            "tags",
            "userData",
          ],
          type: feature.geometry.type,
        });
        for (const [key, value] of Object.entries(
          sanitizeProperties(sanitizedProperties.userData || {}, {
            include: ["createdAt", "age", "userId", "postcode"],
          }),
        )) {
          sanitizedProperties[`userData-${key}`] = value;
        }
        const resolvedIds = resolveIdeaIds({
          properties: sanitizedProperties,
          ideaMap,
        });
        return {
          ...feature,
          properties: sanitizeProperties(resolvedIds, {
            exclude: ["userData", "tags"],
          }),
        };
      })
      .filter(Boolean);
  });
  if (wrapInFeatureCollection) {
    return {
      type: "FeatureCollection",
      features,
    };
  }
  return features;
};

export const prepareQuestionMapResults = (
  {
    geoData,
    question,
    result,
  }: {
    geoData: FeatureCollection;
    question?: Question;
    result?: Partial<Result | QuestionValue>;
  },
  wrapInFeatureCollection = false,
) => {
  if (!geoData?.features?.length) return [{}];
  const stripKeys = [
    "geoDataType",
    "drawType",
    "timeStamp",
    "value",
    "color",
    "choices",
    "description",
  ];
  const features = geoData.features.map(
    (feature: FeatureCollection["features"][number]) => {
      const augmentedProperties = augmentResultProperties({
        properties: feature.properties,
        result,
      });
      const resolvedIds = resolveResultIds({
        properties: augmentedProperties,
        question,
      });
      const sanitizedProperties = sanitizeProperties(resolvedIds, {
        exclude: stripKeys,
        type: feature.geometry.type,
      });
      return {
        ...feature,
        properties: sanitizedProperties,
      };
    },
  );
  if (wrapInFeatureCollection) {
    return {
      type: "FeatureCollection",
      features,
    };
  }
  return features;
};

export const prepareMapResultsPerQuestion = (
  results: Result[],
  question: any,
) => {
  const sanitizedData = results
    ?.flatMap((e) => {
      const value = Object.values(e.values)[0];
      console.log(e, e.values, value);
      const geo = parseGeoData<FeatureCollection>(value?.geoData);
      if (!geo?.features?.length) return;

      // Filter features by area (50 square kilometers)
      const filteredFeatures = geo.features.filter((feature) => {
        if (feature && feature.geometry) {
          // Calculate area in square kilometers
          const area = turf.area(feature) / 1000000; // Convert from square meters to square kilometers

          // Only include features smaller than 50 square kilometers
          return area <= 50;
        }
        return true; // Keep features without geometry
      });

      // Create a new feature collection with only the filtered features
      const filteredGeo = {
        ...geo,
        features: filteredFeatures,
      };

      return prepareQuestionMapResults({
        geoData: filteredGeo,
        question,
        result: { ...e, ...value },
      });
    })
    .filter(Boolean);

  return {
    type: "FeatureCollection",
    features: sanitizedData,
  } as FeatureCollection;
};
